import React, { useState, useEffect } from "react";
import { getAgreement } from "../../../../../api/agreementsApi";
import "./AgreementDetail.css";

/** Этапы по макету (если с бэка не приходят) */
const DEFAULT_STAGES = [
  { name: "Задание на проектирование", key: "design_task" },
  { name: "КП субподрядчика", key: "kp" },
  { name: "Соглашение", key: "agreement" },
  { name: "Договор с субподрядчиком", key: "contract" },
  { name: "Выдача ИРД", key: "ird" },
  { name: "Выполнение работ", key: "work" },
];

const STAGE_STATUS_LABELS = {
  completed: "Завершён",
  in_progress: "В процессе",
  not_started: "Не начат",
};

const PAYMENT_TYPE_LABELS = {
  advance: "Аванс",
  ODGE: "Оплата до госэкспертизы (ОДГЭ)",
  OPGE: "Оплата после госэкспертизы (ОПГЭ)",
};

const PAYMENT_STATUS_LABELS = {
  overdue: "Просрочен",
  expected: "Ожидается",
  paid: "Оплачен",
  paid_overdue: "Оплачен с просрочкой",
  expected_ODGE: "Ожидается ОДГЭ",
  expected_OPGE: "Ожидается ОПГЭ",
  overdue_OPGE: "Просрочен ОПГЭ",
  paid_advance: "Оплачен Аванс",
};

const AgreementDetail = ({ agreementId, inline = false, fallbackCounterpartyName = null }) => {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!agreementId) {
      setAgreement(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAgreement(agreementId)
      .then((data) => {
        if (!cancelled) setAgreement(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || "Не удалось загрузить соглашение");
          setAgreement(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [agreementId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString("ru-RU");
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("ru-RU").format(amount);
  };

  const getPaymentStatusClass = (status) => {
    if (!status) return "";
    if (status === "overdue" || status === "paid_overdue" || status === "overdue_OPGE" || status === "overdue_advance") return "agreement-detail-status--overdue";
    if (status === "expected" || status === "expected_ODGE" || status === "expected_OPGE") return "agreement-detail-status--expected";
    if (status === "paid" || status === "paid_advance") return "agreement-detail-status--paid";
    return "";
  };

  const counterpartyName =
    agreement?.counterparty_summary?.display_name ??
    agreement?.counterparty_summary?.full_name ??
    agreement?.counterparty_summary?.full_company_name ??
    agreement?.counterparty_summary?.short_company_name ??
    agreement?.counterparty_name ??
    agreement?.company_name ??
    fallbackCounterpartyName ??
    (Array.isArray(agreement?.performer_names) && agreement.performer_names.length > 0
      ? agreement.performer_names.filter(Boolean).join(", ")
      : null) ??
    (agreement?.counterparty_id != null
      ? `Контрагент #${agreement.counterparty_id}`
      : agreement?.company_id != null
        ? `Компания #${agreement.company_id}`
        : "Соглашение");

  const stages = Array.isArray(agreement?.stages) && agreement.stages.length > 0
    ? agreement.stages
    : DEFAULT_STAGES.map((s, i) => ({ ...s, status: i < 4 ? "completed" : "not_started" }));

  const currentStage = agreement?.current_stage ?? {};
  const payments = Array.isArray(agreement?.payments) && agreement.payments.length > 0
    ? agreement.payments
    : [
        { payment_type: "advance", percent: 30, plan_amount: (agreement?.price ?? 0) * 0.3, fact_amount: null, plan_date: null, fact_date: null, status: "overdue" },
        { payment_type: "ODGE", percent: 50, plan_amount: (agreement?.price ?? 0) * 0.5, fact_amount: null, plan_date: null, fact_date: null, status: "expected" },
        { payment_type: "OPGE", percent: 20, plan_amount: (agreement?.price ?? 0) * 0.2, fact_amount: null, plan_date: null, fact_date: null, status: null },
      ];

  const blockLabels = (agreement?.block_ids?.length ? agreement.block_ids : []).slice(0, 2);
  const overallPaymentStatus = agreement?.status ?? "expected_ODGE";

  if (!agreementId) return null;

  const content = (
    <>
      {loading && (
        <div className="agreement-detail-loading">Загрузка...</div>
      )}

      {error && (
        <div className="agreement-detail-error" role="alert">
          {error}
        </div>
      )}

      {!loading && agreement && (
        <>
            <h2 className="agreement-detail-title">
              Этапы по соглашению с {counterpartyName}
            </h2>

            <div className="agreement-detail-timeline">
              {stages.map((stage, idx) => (
                <div
                  key={stage.key ?? stage.id ?? idx}
                  className={`agreement-detail-timeline-step agreement-detail-timeline-step--${stage.status ?? "not_started"}`}
                >
                  <div className="agreement-detail-timeline-icon">
                    {(stage.status === "completed") ? "✓" : (idx + 1)}
                  </div>
                  <div className="agreement-detail-timeline-label">
                    {stage.name}
                  </div>
                  <div className="agreement-detail-timeline-status">
                    {STAGE_STATUS_LABELS[stage.status] ?? "Не начат"}
                  </div>
                  {idx < stages.length - 1 && <div className="agreement-detail-timeline-connector" />}
                </div>
              ))}
            </div>

            <section className="agreement-detail-section">
              <h3 className="agreement-detail-section-title">ЭТАП</h3>
              <div className="agreement-detail-stage-grid">
                <div className="agreement-detail-stage-item">
                  <label>Дата начала (план)</label>
                  <div>{formatDate(currentStage.plan_start)}</div>
                </div>
                <div className="agreement-detail-stage-item">
                  <label>Дата окончания (план)</label>
                  <div className={currentStage.plan_end_overdue ? "agreement-detail-date-overdue" : ""}>
                    {formatDate(currentStage.plan_end)}
                    {currentStage.plan_end_overdue && " (просрочено)"}
                  </div>
                </div>
                <div className="agreement-detail-stage-item">
                  <label>Дата начала (факт)</label>
                  <div>{formatDate(currentStage.fact_start)}</div>
                </div>
                <div className="agreement-detail-stage-item">
                  <label>Дата окончания (факт)</label>
                  <div>{formatDate(currentStage.fact_end)}</div>
                </div>
                <div className="agreement-detail-stage-item agreement-detail-stage-item--full">
                  <label>Статус</label>
                  <span className={`agreement-detail-status-badge agreement-detail-status-badge--${currentStage.status ?? "in_progress"}`}>
                    {STAGE_STATUS_LABELS[currentStage.status] ?? "В процессе"}
                  </span>
                </div>
              </div>
              {currentStage.alert_message && (
                <div className="agreement-detail-alert" role="alert">
                  <span className="agreement-detail-alert-icon">ℹ</span>
                  {currentStage.alert_message}
                </div>
              )}
            </section>

            <section className="agreement-detail-section">
              <h3 className="agreement-detail-section-title">СТАТУС ОПЛАТЫ</h3>
              <div className="agreement-detail-table-wrap">
                <table className="agreement-detail-table">
                  <thead>
                    <tr>
                      <th>Платёж</th>
                      <th>%</th>
                      <th>Сумма (план)</th>
                      <th>Сумма (факт)</th>
                      <th>Дата (план)</th>
                      <th>Дата (факт)</th>
                      <th>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((row, i) => (
                      <tr key={row.payment_type ?? i}>
                        <td>{PAYMENT_TYPE_LABELS[row.payment_type] ?? row.payment_type ?? "—"}</td>
                        <td>{row.percent != null ? `${row.percent}%` : "—"}</td>
                        <td>{formatCurrency(row.plan_amount)} ₸</td>
                        <td>{formatCurrency(row.fact_amount)}</td>
                        <td>{formatDate(row.plan_date)}</td>
                        <td>{formatDate(row.fact_date)}</td>
                        <td>
                          {row.status ? (
                            <span className={`agreement-detail-status ${getPaymentStatusClass(row.status)}`}>
                              {PAYMENT_STATUS_LABELS[row.status] ?? row.status}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="agreement-detail-footer">
              <div className="agreement-detail-footer-left">
                <span className="agreement-detail-footer-subcontractor">
                  &gt; {counterpartyName}
                </span>
                {blockLabels.length > 0 && (
                  <span className="agreement-detail-footer-blocks">
                    {blockLabels.map((b, i) => (
                      <span key={i} className="agreement-detail-footer-block-tag">{b}</span>
                    ))}
                  </span>
                )}
                <span className="agreement-detail-footer-total">
                  ₸ {formatCurrency(agreement.price)}
                </span>
              </div>
              <div className="agreement-detail-footer-right">
                <span className="agreement-detail-footer-stage-badge">
                  {stages[5]?.name ?? "Выполнение работ"} · В процессе
                </span>
                <span className={`agreement-detail-status agreement-detail-status--footer ${getPaymentStatusClass(overallPaymentStatus)}`}>
                  {PAYMENT_STATUS_LABELS[overallPaymentStatus] ?? overallPaymentStatus ?? "Ожидается ОДГЭ"}
                </span>
                <button type="button" className="agreement-detail-action view-btn" title="Просмотр">
                  👁
                </button>
                <button type="button" className="agreement-detail-action edit-btn" title="Редактировать">
                  ✎
                </button>
              </div>
            </div>
          </>
        )}
    </>
  );

  return <div className={inline ? "agreement-detail-inline" : "agreement-detail-modal"}>{content}</div>;
};

export default AgreementDetail;
